import { AuthEndpoint, finishEncryptionEndpoint, startEncryptionEndpoint } from '@datarock/api-client';
import { StatusCodes } from 'http-status-codes';
import { VResponseCode } from '@datarock/protobuffers';
import encryptionStrings from '../constants/encryptionFlowStrings';
import { retryWithBackoff } from '../utils';
import { validateAccountToken } from './authService';
/**
 * Validates that the account has a valid token
 */
export async function validateToken(input) {
    const { account, controller, authService } = input;
    const result = await validateAccountToken(account, controller, authService);
    if ('requiresManualLogin' in result) {
        return result;
    }
    return { token: result.token };
}
/**
 * Starts the encryption process with the server
 */
export async function startEncryption(input) {
    var _a, _b;
    const { controller, token, account } = input;
    if (!controller) {
        throw new Error(encryptionStrings.errorControllerNotAvailable);
    }
    // Set the authentication token for the API call
    if (token === null || token === void 0 ? void 0 : token.accessToken) {
        AuthEndpoint.accessToken = token.accessToken;
    }
    try {
        const startResponse = await retryWithBackoff(() => controller.fetch(startEncryptionEndpoint, {}), {
            maxAttempts: 3,
            shouldRetry: (error) => {
                // Don't retry 401 authentication errors
                if ((error === null || error === void 0 ? void 0 : error.status) === StatusCodes.UNAUTHORIZED) {
                    // Return the account to prompt for login
                    return {
                        requiresManualLogin: true,
                        authorizedAccount: account
                    };
                }
                // Retry all other errors
                return true;
            }
        });
        // Clear the access token from the endpoint after the request
        if (token === null || token === void 0 ? void 0 : token.accessToken) {
            AuthEndpoint.accessToken = undefined;
        }
        // Type guard to ensure accessCheck has the expected structure
        if (startResponse &&
            typeof startResponse === 'object' &&
            'requiresManualLogin' in startResponse) {
            return startResponse;
        }
        if (((_a = startResponse.response) === null || _a === void 0 ? void 0 : _a.code) !== VResponseCode.SUCCESS) {
            throw new Error(((_b = startResponse.response) === null || _b === void 0 ? void 0 : _b.text) ||
                encryptionStrings.errorEncryptionStartFailed);
        }
        if (!startResponse.serverSeed) {
            throw new Error(encryptionStrings.errorNoStartResponse);
        }
        return { startResponse };
    }
    catch (error) {
        if (error instanceof Error) {
            throw error;
        }
        throw new Error(encryptionStrings.errorEncryptionStartFailed);
    }
}
/**
 * Finishes the encryption process with the server
 */
export async function finishEncryption(input) {
    var _a, _b;
    const { controller, startResponse, encryptionResult, vaultSettings, position, fileName, token, account } = input;
    if (!controller) {
        throw new Error(encryptionStrings.errorControllerNotAvailable);
    }
    // Set the authentication token for the API call
    if (token === null || token === void 0 ? void 0 : token.accessToken) {
        AuthEndpoint.accessToken = token.accessToken;
    }
    try {
        const finishResponse = await retryWithBackoff(() => controller.fetch(finishEncryptionEndpoint, {
            allowCopy: vaultSettings.allowCopy,
            allowForward: vaultSettings.allowForward,
            allowPrint: vaultSettings.allowPrint,
            allowSave: vaultSettings.allowSave,
            authorizedUsers: vaultSettings.authorizedUsers,
            biometricsRequired: vaultSettings.biometricsRequired,
            countries: vaultSettings.countries.length > 0
                ? vaultSettings.countries
                : undefined,
            enableDecryptReceipts: vaultSettings.enableDecryptReceipts,
            fileId: startResponse.fileId,
            fileName: fileName.replace(/^.*[\\\/]/, ''),
            hash: encryptionResult.hash,
            latitude: position.coords.latitude.toString(),
            longitude: position.coords.longitude.toString(),
            multifactorGeolocation: false,
            notesIncluded: vaultSettings.notes.length > 0,
            secureFiles: encryptionResult.secureFileList,
            sunriseDate: vaultSettings.sunriseDate || undefined,
            sunsetDate: vaultSettings.sunsetDate || undefined,
            totalSizeInBytes: encryptionResult.totalSize
        }), {
            maxAttempts: 3,
            shouldRetry: (error) => {
                // Don't retry 401 authentication errors
                if ((error === null || error === void 0 ? void 0 : error.status) === 401) {
                    // Return the account to prompt for login
                    return {
                        requiresManualLogin: true,
                        authorizedAccount: account
                    };
                }
                // Retry all other errors
                return true;
            }
        });
        // Clear the access token from the endpoint after the request
        if (token === null || token === void 0 ? void 0 : token.accessToken) {
            AuthEndpoint.accessToken = undefined;
        }
        if (((_a = finishResponse.response) === null || _a === void 0 ? void 0 : _a.code) !== VResponseCode.SUCCESS) {
            throw new Error(((_b = finishResponse.response) === null || _b === void 0 ? void 0 : _b.text) ||
                encryptionStrings.errorEncryptionFinishFailed);
        }
        return { success: true };
    }
    catch (error) {
        if (error instanceof Error) {
            throw error;
        }
        throw new Error(encryptionStrings.errorEncryptionFinishFailed);
    }
}
